import type { KeyboardEvent } from 'react';

const FOCAVEIS =
  'input:not([type=hidden]):not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled)';

/**
 * Tira um campo da sequência do Enter (cidade já preenchida, referência, taxa que a regra calcula):
 * o Tab e o clique continuam chegando nele. Uso: `<input {...PULAR_NO_ENTER} />`.
 */
export const PULAR_NO_ENTER = { 'data-enter': 'pular' } as const;

const TIPOS_SEM_DIGITACAO = new Set(['button', 'submit', 'reset', 'image']);

/**
 * Foco num controle de formulário: aí as letras soltas são do campo (digitação, P/D/C/O do
 * pagamento), nunca atalho da tela. Rádio e caixa de marcar contam: fazem parte do formulário.
 */
export function ehCampoDeEdicao(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) return false;
  if (alvo.isContentEditable) return true;
  if (alvo instanceof HTMLTextAreaElement || alvo instanceof HTMLSelectElement) return true;
  return alvo instanceof HTMLInputElement && !TIPOS_SEM_DIGITACAO.has(alvo.type);
}

/**
 * Teclado do balcão, para o formulário inteiro:
 * - Enter num campo de uma linha → vai para o próximo campo (não envia sem querer);
 * - Enter no texto do pedido (textarea) → também avança; Shift+Enter quebra a linha;
 * - campo com `data-enter="pular"` fica fora da sequência do Enter;
 * - Ctrl/⌘ + Enter em qualquer lugar → envia.
 * Rádios, combobox e a rua tratam o Enter deles antes (defaultPrevented).
 */
export function tecladoDoFormulario(e: KeyboardEvent<HTMLFormElement>): void {
  if (e.key !== 'Enter' || e.defaultPrevented || e.nativeEvent.isComposing) return;
  const alvo = e.target as HTMLElement;
  const form = e.currentTarget;

  if (e.ctrlKey || e.metaKey) {
    e.preventDefault();
    form.requestSubmit();
    return;
  }
  if (!avancaNoEnter(alvo, e.shiftKey)) return;

  e.preventDefault();
  const campos = Array.from(form.querySelectorAll<HTMLElement>(FOCAVEIS)).filter(
    (el) => el === alvo || (el.offsetParent !== null && el.dataset.enter !== 'pular'),
  );
  const proximo = campos[campos.indexOf(alvo) + 1];
  proximo?.focus();
}

function avancaNoEnter(alvo: HTMLElement, shift: boolean): boolean {
  if (alvo instanceof HTMLTextAreaElement) return !shift;
  return alvo instanceof HTMLInputElement && alvo.type !== 'submit' && alvo.type !== 'checkbox';
}
